import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import '../../styles/dashboard-navigation.css';

// Define the dashboard type
export type DashboardType =
  | 'asin-review-history'
  | 'ppc-situation-history'
  | 'ppc-basics'
  | 'client-notes-global'
  | 'client-notes-asin'
  | 'ppc-status-history'
  | 'margin'
  | 'price-history'
  | 'full-margin-history'
  | 'global-bids'
  | 'problems'
  | 'inventory'
  | 'out-of-stocks' // Keep one
  // | 'out-of-stocks' // Remove duplicate
  | 'season'
  | 'listing'
  | 'latest-gpo-results'
  | 'gpo-performance'
  | 'gpo-history'
  | 'targets'
  | 'farming-basics'
  | 'farming-seeds' // Keep one
  // | 'farming-seeds' // Remove duplicate
  | 'farming-results'
  | 'farming-bid-history'
  | 'overall-results'
  | 'ppc-results'
  | 'family-situation'
  | 'lifetime-results'
  | 'serp-ranks'
  | 'dayparting'
  | 'latest-experiment'
  | 'campaigns'
  | 'campaign-history'
  | '4wk-trend-campaign'
  | 'campaign-status-history'
  | 'placement-transition-inputs'
  | 'placement-transition-results'
  | '4-week-ppc-charts'
  | '4-week-revenue-profit-chart'
  | 'month-over-month-charts'
  | 'asin-results-by-month'
  | 'year-over-year-change'
  | 'month-over-month-change'
  | 'dayparting'
  | 'bundle-ledger-viewer'
  | null;

// Define the dashboard info type
export interface DashboardInfo {
  label: string;
  value: DashboardType;
}

// Create an ordered array of all dashboards following the dropdown menu order
export const allDashboards: DashboardInfo[] = [
  // Notes
  { label: 'Client Notes - Global', value: 'client-notes-global' },
  { label: 'Client Notes - ASIN', value: 'client-notes-asin' },
  { label: 'ASIN Review History', value: 'asin-review-history' },
  { label: 'Overall PPC Situation History', value: 'ppc-situation-history' },
  
  // Basics 1
  { label: 'PPC Basics', value: 'ppc-basics' },
  { label: 'PPC Status History', value: 'ppc-status-history' },
  { label: 'Margin', value: 'margin' },
  { label: 'Price History', value: 'price-history' },
  { label: 'Full Margin History', value: 'full-margin-history' },
  
  // Basics 2
  { label: 'Global Bids', value: 'global-bids' },
  { label: 'Problems', value: 'problems' },
  { label: 'Inventory', value: 'inventory' },
  { label: 'Out of Stocks', value: 'out-of-stocks' },
  { label: 'Season', value: 'season' },
  { label: 'Listing', value: 'listing' },
  
  // GPOs
  { label: 'Latest GPO Results', value: 'latest-gpo-results' },
  { label: 'GPO Performance', value: 'gpo-performance' },
  { label: 'GPO History', value: 'gpo-history' },
  
  // Farming
  { label: 'Targets', value: 'targets' },
  { label: 'Farming Basics', value: 'farming-basics' },
  { label: 'Farming Seeds', value: 'farming-seeds' },
  { label: 'Farming Results', value: 'farming-results' },
  { label: 'Farming Bid History', value: 'farming-bid-history' },
  
  // Overall Results
  { label: 'Overall Results', value: 'overall-results' },
  { label: 'PPC Results', value: 'ppc-results' },
  { label: 'Family Situation', value: 'family-situation' },
  { label: 'Lifetime Results', value: 'lifetime-results' },
  { label: 'SERP Ranks', value: 'serp-ranks' },
  { label: 'Dayparting', value: 'dayparting' },
  { label: 'Latest Experiment (GP Only)', value: 'latest-experiment' },
  
  // Campaign Results
  { label: 'Campaigns', value: 'campaigns' },
  { label: 'Campaign History', value: 'campaign-history' },
  { label: '4wk Trend / Campaign', value: '4wk-trend-campaign' },
  { label: 'Campaign Status History', value: 'campaign-status-history' },
  { label: 'Placement Transition Inputs', value: 'placement-transition-inputs' },
  
  // Period Results
  { label: 'Placement Transition Results', value: 'placement-transition-results' },
  { label: '4-Week PPC Charts', value: '4-week-ppc-charts' },
  { label: '4 Week Revenue and Profit Chart', value: '4-week-revenue-profit-chart' },
  { label: 'Month Over Month Charts', value: 'month-over-month-charts' },
  { label: 'ASIN\'s Results by Month', value: 'asin-results-by-month' },
  { label: 'Year Over Year Change', value: 'year-over-year-change' },
  { label: 'Month Over Month Change', value: 'month-over-month-change' },
  
  // Ledger Viewer
  { label: 'Bundle Ledger Viewer', value: 'bundle-ledger-viewer' },
];

interface DashboardNavigationProps {
  currentDashboard: DashboardType;
  onNavigate: (dashboard: DashboardType) => void;
}

/**
 * DashboardNavigation component
 * Displays Previous/Next buttons for navigating between dashboards
 * with dashboard names to the left and right of the buttons
 */
const DashboardNavigation: React.FC<DashboardNavigationProps> = ({
  currentDashboard,
  onNavigate
}) => {
  // Only render if a dashboard is selected
  if (!currentDashboard) return null;

  // Find current dashboard info
  const currentIndex = allDashboards.findIndex(d => d.value === currentDashboard);

  // If dashboard not found in the list, don't render navigation
  if (currentIndex === -1) return null;

  // Determine prev/next dashboards with wrap-around
  const prevIndex = (currentIndex - 1 + allDashboards.length) % allDashboards.length;
  const nextIndex = (currentIndex + 1) % allDashboards.length;

  const prevDashboard = allDashboards[prevIndex];
  const nextDashboard = allDashboards[nextIndex];

  return (
    <div className="dashboard-nav-container">
      {/* Previous dashboard name */}
      <div className="dashboard-name-container dashboard-name-left">
        <span className="dashboard-nav-label">{prevDashboard.label}</span>
      </div>

      {/* Fixed-width container for buttons */}
      <div className="dashboard-buttons-container">
        <button
          className="dashboard-nav-button"
          onClick={() => onNavigate(prevDashboard.value)}
          aria-label={`Navigate to ${prevDashboard.label}`}
        >
          <ArrowLeft size={16} />
          <span>Previous</span>
        </button>

        <button
          className="dashboard-nav-button"
          onClick={() => onNavigate(nextDashboard.value)}
          aria-label={`Navigate to ${nextDashboard.label}`}
        >
          <span>Next</span>
          <ArrowRight size={16} />
        </button>
      </div>

      {/* Next dashboard name */}
      <div className="dashboard-name-container dashboard-name-right">
        <span className="dashboard-nav-label">{nextDashboard.label}</span>
      </div>
    </div>
  );
};

export default DashboardNavigation;